import React from "react";
import {
  TOTAL_LESSONS,
  MASTERY_MAX,
  historySeries,
  conceptStats,
  keyStats,
  masteryTotals,
  chordFor,
} from "../../lib/numberSenseData";
import { shellVoicing } from "../../lib/keyboardPlan";
import { Icon, Keys } from "../numberSenseUi";

/* ============================================================
   Number Sense — stats / history screen
   Reads straight from persisted progress: the XP trend from the
   per-day `history` log, accuracy roll-ups from the per-concept
   `mastery` map. All derivation lives in pure helpers
   (numberSenseData) so this stays presentational.
   ============================================================ */

// A short "M/D" label for a "YYYY-M-D" day key.
const shortDay = (key) => {
  const parts = String(key).split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : key;
};

// Strength shown as MASTERY_MAX little pips, filled up to the current box.
const StrengthPips = ({ strength }) => (
  <span className="ns-pips" aria-label={`strength ${strength} of ${MASTERY_MAX}`}>
    {Array.from({ length: MASTERY_MAX }, (_, i) => (
      <i key={i} className={i < strength ? "on" : ""} />
    ))}
  </span>
);

const StatsScreen = ({ progress, doneLessons, onBack }) => {
  const mastery = progress.mastery || {};
  const totals = masteryTotals(mastery);
  const byKey = keyStats(mastery);
  const allConcepts = conceptStats(mastery);
  // hearing a chord (`key:deg:ear`) and voicing its shell (`key:deg:shell`)
  // are tracked apart from knowing it — each gets its own block below
  const concepts = allConcepts.filter((c) => !c.ear && !c.shell);
  const earConcepts = allConcepts.filter((c) => c.ear);
  const shellConcepts = allConcepts.filter((c) => c.shell);
  const series = historySeries(progress.history || {}).slice(-14);
  const maxXp = Math.max(1, ...series.map((d) => d.xp));
  const hasData = totals.seen > 0 || series.length > 0;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">your stats</div>
          <div className="stat xp">
            <Icon name="bolt" />
            {progress.xp}
          </div>
        </div>
        <div className="scroll">
          {!hasData ? (
            <div className="ns-stats-empty">
              <Keys />
              <p>
                no stats yet — finish a lesson or play the arcade and your
                progress will start charting here.
              </p>
            </div>
          ) : (
            <>
              <div className="ns-stat-cards">
                <div className="ns-stat-card">
                  <div className="k">total XP</div>
                  <div className="v">{progress.xp}</div>
                </div>
                <div className="ns-stat-card">
                  <div className="k">day streak</div>
                  <div className="v">{progress.streak}</div>
                </div>
                <div className="ns-stat-card">
                  <div className="k">lessons</div>
                  <div className="v">
                    {doneLessons}
                    <small>/{TOTAL_LESSONS}</small>
                  </div>
                </div>
                <div className="ns-stat-card">
                  <div className="k">mastered</div>
                  <div className="v">
                    {totals.mastered}
                    <small>/{totals.tracked}</small>
                  </div>
                </div>
                <div className="ns-stat-card wide">
                  <div className="k">overall accuracy</div>
                  <div className="v">
                    {totals.pct}
                    <small>%</small>
                  </div>
                  <div className="ns-meter">
                    <i style={{ width: `${totals.pct}%` }} />
                  </div>
                </div>
              </div>

              <div className="ns-stat-block">
                <div className="ns-stat-h">
                  XP earned · last {series.length} day
                  {series.length === 1 ? "" : "s"}
                </div>
                {series.length ? (
                  <div className="ns-trend" role="img" aria-label="XP earned per day">
                    {series.map((d) => (
                      <div className="ns-trend-col" key={d.date}>
                        <div className="bar-wrap">
                          <span className="amt">{d.xp}</span>
                          <i
                            style={{
                              height: `${Math.max(
                                4,
                                Math.round((d.xp / maxXp) * 100)
                              )}%`,
                            }}
                          />
                        </div>
                        <div className="day">{shortDay(d.date)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="ns-stat-note">play a little to start the trend.</p>
                )}
              </div>

              {byKey.length > 0 && (
                <div className="ns-stat-block">
                  <div className="ns-stat-h">accuracy by key</div>
                  <div className="ns-keystats">
                    {byKey.map((k) => (
                      <div className="ns-keyrow" key={k.key}>
                        <div className="lbl">
                          <b>{k.key}</b>
                          <small>
                            {k.mastered}/{k.concepts} mastered
                            {k.avgMs != null &&
                              ` · ~${(k.avgMs / 1000).toFixed(1)}s an answer`}
                          </small>
                        </div>
                        <div className="ns-meter">
                          <i style={{ width: `${k.pct}%` }} />
                        </div>
                        <div className="pct">{k.pct}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {concepts.length > 0 && (
                <div className="ns-stat-block">
                  <div className="ns-stat-h">every chord you've drilled</div>
                  <div className="ns-conceptgrid">
                    {concepts.map((c) => (
                      <div className="ns-conceptcard" key={c.concept}>
                        <div className="head">
                          <span className="num mono">
                            {c.key}: {c.deg}
                          </span>
                          <span className="chord">{chordFor(c.key, c.deg)}</span>
                        </div>
                        <StrengthPips strength={c.strength} />
                        <div className="acc">
                          {c.pct}%{" "}
                          <small>
                            ({c.correct}/{c.seen})
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {shellConcepts.length > 0 && (
                <div className="ns-stat-block">
                  <div className="ns-stat-h">your shells — the rootless voicings</div>
                  <div className="ns-conceptgrid">
                    {shellConcepts.map((c) => (
                      <div className="ns-conceptcard" key={c.concept}>
                        <div className="head">
                          <span className="num mono">
                            {c.key}: {c.deg}
                          </span>
                          <span className="chord">
                            {shellVoicing(c.key, c.deg).join("–") ||
                              chordFor(c.key, c.deg)}
                          </span>
                        </div>
                        <StrengthPips strength={c.strength} />
                        <div className="acc">
                          {c.pct}%{" "}
                          <small>
                            ({c.correct}/{c.seen})
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {earConcepts.length > 0 && (
                <div className="ns-stat-block">
                  <div className="ns-stat-h">your ear, chord by chord</div>
                  <div className="ns-conceptgrid">
                    {earConcepts.map((c) => (
                      <div className="ns-conceptcard" key={c.concept}>
                        <div className="head">
                          <span className="num mono">
                            {c.key}: {c.deg}
                          </span>
                          <span className="chord">
                            <Icon name="ear" />
                          </span>
                        </div>
                        <StrengthPips strength={c.strength} />
                        <div className="acc">
                          {c.pct}%{" "}
                          <small>
                            ({c.correct}/{c.seen})
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsScreen;
